import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import GoogleAuthButton from "../../Components/Common/GoogleAuthButton";
import { GoogleLogo } from "../../Components/Common/GoogleLogo";
import {
  clearPreferredTenantId,
  getPreferredTenantId,
  isMembershipEnterable,
  setPreferredTenantId,
  StudioMembership,
} from "../../helpers/identity_storage";
import { resolveAuthClientError } from "../../helpers/authClientError";
import {
  getMemberships,
  unlinkGoogleAccount,
} from "../../services/authService";
import { getProfile, updateProfile } from "../../services/profileService";
import { TENANT_LOCALE_OPTIONS } from "./regionalSettingsOptions";

const LANGUAGE_STORAGE_KEY = "I18N_LANGUAGE";

interface AccountFormValues {
  name: string;
  email: string;
  locale: string;
  current_password: string;
  password: string;
  password_confirmation: string;
}

type ProfileApiError = {
  data?: string;
  details?: Record<string, string[]>;
};

const AccountSettings = () => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [memberships, setMemberships] = useState<StudioMembership[]>([]);
  const [preferredTenantId, setPreferredTenantIdState] = useState<string>("");
  const [savingPreferred, setSavingPreferred] = useState(false);
  const [preferredMessage, setPreferredMessage] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState(true);
  const [googleLinked, setGoogleLinked] = useState(false);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);

  useEffect(() => {
    document.title = t("settings.account.pageTitle");
  }, [t]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t("settings.account.validation.nameRequired")),
        email: Yup.string()
          .email(t("settings.account.validation.emailInvalid"))
          .required(t("settings.account.validation.emailRequired")),
        locale: Yup.string().required(t("settings.account.validation.localeRequired")),
        current_password: Yup.string().when("password", {
          is: (value: string) => Boolean(value?.length) && hasPassword,
          then: (schema) =>
            schema.required(t("settings.account.validation.currentPasswordRequired")),
          otherwise: (schema) => schema,
        }),
        password: Yup.string()
          .nullable()
          .transform((value) => (value === "" ? null : value))
          .min(8, t("settings.account.validation.passwordMin")),
        password_confirmation: Yup.string().when("password", {
          is: (value: string) => Boolean(value?.length),
          then: (schema) =>
            schema
              .required(t("settings.account.validation.passwordConfirmationRequired"))
              .oneOf(
                [Yup.ref("password")],
                t("settings.account.validation.passwordConfirmationMismatch")
              ),
          otherwise: (schema) => schema,
        }),
      }),
    [t, hasPassword]
  );

  const form = useFormik<AccountFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      locale: "pt-BR",
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      setFormError(null);

      try {
        const payload = {
          name: values.name.trim(),
          email: values.email.trim(),
          locale: values.locale,
          ...(values.password
            ? {
                password: values.password,
                password_confirmation: values.password_confirmation,
                ...(hasPassword
                  ? { current_password: values.current_password }
                  : {}),
              }
            : {}),
        };

        const response = await updateProfile(payload);

        setHasPassword(
          response.data.has_password === undefined
            ? hasPassword || Boolean(values.password)
            : Boolean(response.data.has_password)
        );

        helpers.setValues({
          name: response.data.name,
          email: response.data.email,
          locale: response.data.locale || values.locale,
          current_password: "",
          password: "",
          password_confirmation: "",
        });
        helpers.setTouched({});

        if (response.data.locale && response.data.locale !== i18n.language) {
          await i18n.changeLanguage(response.data.locale);
          localStorage.setItem(LANGUAGE_STORAGE_KEY, response.data.locale);
        }

        toast.success(response.message || t("settings.account.toast.saveSuccess"));
      } catch (error: unknown) {
        const apiError = error as ProfileApiError;

        if (apiError.details) {
          Object.entries(apiError.details).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages[0]) {
              helpers.setFieldError(field, messages[0]);
            }
          });
        }

        setFormError(apiError.data || t("settings.account.toast.saveError"));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  // Formik's `form` object is a new reference every render — never put it in
  // effect deps (that + setValues caused an infinite GET /users/me + /memberships loop).
  const setValuesRef = useRef(form.setValues);
  setValuesRef.current = form.setValues;

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setFormError(null);

    try {
      const [response, membershipsResponse] = await Promise.all([
        getProfile(),
        getMemberships(),
      ]);

      setValuesRef.current({
        name: response.data.name,
        email: response.data.email,
        locale: response.data.locale || i18n.language || "pt-BR",
        current_password: "",
        password: "",
        password_confirmation: "",
      });
      setHasPassword(response.data.has_password !== false);
      setGoogleLinked(Boolean(response.data.google_linked));
      setGoogleEmail(response.data.google_email ?? null);

      const active = (membershipsResponse.data?.memberships || []).filter(
        (item) => isMembershipEnterable(item)
      );
      setMemberships(active);

      const preferred = getPreferredTenantId();
      const preferredStillValid =
        preferred != null && active.some((item) => item.tenant_id === preferred);

      setPreferredTenantIdState(
        preferredStillValid && preferred != null ? String(preferred) : ""
      );

      if (preferred != null && !preferredStillValid) {
        clearPreferredTenantId();
      }
    } catch (error: unknown) {
      const apiError = error as ProfileApiError;
      setFormError(apiError.data || t("settings.account.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  const handleSavePreferredStudio = () => {
    setSavingPreferred(true);
    setPreferredMessage(null);

    try {
      if (!preferredTenantId) {
        clearPreferredTenantId();
        setPreferredMessage(t("settings.account.preferredStudio.cleared"));
        toast.success(t("settings.account.preferredStudio.cleared"));
        return;
      }

      setPreferredTenantId(Number(preferredTenantId));
      setPreferredMessage(t("settings.account.preferredStudio.saved"));
      toast.success(t("settings.account.preferredStudio.saved"));
    } finally {
      setSavingPreferred(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (
      !window.confirm(t("settings.account.google.unlinkConfirm"))
    ) {
      return;
    }

    setUnlinkingGoogle(true);

    try {
      const response = await unlinkGoogleAccount();
      setGoogleLinked(Boolean(response.data?.google_linked));
      setGoogleEmail(response.data?.google_email ?? null);
      toast.success(
        response.message || t("settings.account.google.toast.unlinked")
      );
    } catch (error: unknown) {
      const resolved = resolveAuthClientError(
        error,
        t("settings.account.google.toast.unlinkError")
      );
      toast.error(resolved.message);
    } finally {
      setUnlinkingGoogle(false);
    }
  };

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const renderPasswordToggle = (
    visible: boolean,
    onToggle: () => void,
    inputId: string
  ) => (
    <button
      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
      type="button"
      aria-label={t("settings.account.fields.password.toggleVisibility")}
      aria-controls={inputId}
      onClick={onToggle}
    >
      <i className={`ri-${visible ? "eye-off" : "eye"}-fill align-middle`} />
    </button>
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("settings.account.breadcrumb.title")}
          pageTitle={t("settings.account.breadcrumb.pageTitle")}
        />
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("settings.account.card.title")}</h5>
              </CardHeader>
              <CardBody>
                {formError ? (
                  <Alert color="danger" className="mb-3">
                    {formError}
                  </Alert>
                ) : null}

                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <Form
                    onSubmit={(event) => {
                      event.preventDefault();
                      form.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Label htmlFor="account-name" className="form-label">
                        {t("settings.account.fields.name.label")}
                      </Label>
                      <Input
                        id="account-name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={form.values.name}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        invalid={!!(form.touched.name && form.errors.name)}
                      />
                      {form.touched.name && form.errors.name ? (
                        <FormFeedback type="invalid">{form.errors.name}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label htmlFor="account-email" className="form-label">
                        {t("settings.account.fields.email.label")}
                      </Label>
                      <Input
                        id="account-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.values.email}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        invalid={!!(form.touched.email && form.errors.email)}
                      />
                      {form.touched.email && form.errors.email ? (
                        <FormFeedback type="invalid">{form.errors.email}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="account-locale" className="form-label">
                        {t("settings.account.fields.locale.label")}
                      </Label>
                      <Input
                        id="account-locale"
                        name="locale"
                        type="select"
                        value={form.values.locale}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        invalid={!!(form.touched.locale && form.errors.locale)}
                      >
                        {TENANT_LOCALE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </Input>
                      <p className="text-muted small mt-2 mb-0">
                        {t("settings.account.fields.locale.help")}
                      </p>
                      {form.touched.locale && form.errors.locale ? (
                        <FormFeedback type="invalid">{form.errors.locale}</FormFeedback>
                      ) : null}
                    </div>

                    <h6 className="mb-3">{t("settings.account.passwordSection.title")}</h6>
                    <p className="text-muted small mb-3">
                      {hasPassword
                        ? t("settings.account.passwordSection.help")
                        : t("settings.account.passwordSection.helpSetFirst")}
                    </p>
                    {!hasPassword ? (
                      <Alert color="info" className="mb-3" role="status">
                        {t("settings.account.passwordSection.otpOnlyHint")}
                      </Alert>
                    ) : null}

                    {hasPassword ? (
                      <div className="mb-3 position-relative auth-pass-inputgroup">
                        <Label htmlFor="account-current-password" className="form-label">
                          {t("settings.account.fields.currentPassword.label")}
                        </Label>
                        <Input
                          id="account-current-password"
                          name="current_password"
                          type={showCurrentPassword ? "text" : "password"}
                          autoComplete="current-password"
                          value={form.values.current_password}
                          onChange={form.handleChange}
                          onBlur={form.handleBlur}
                          invalid={
                            !!(form.touched.current_password && form.errors.current_password)
                          }
                        />
                        {renderPasswordToggle(
                          showCurrentPassword,
                          () => setShowCurrentPassword((value) => !value),
                          "account-current-password"
                        )}
                        {form.touched.current_password && form.errors.current_password ? (
                          <FormFeedback type="invalid">
                            {form.errors.current_password}
                          </FormFeedback>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="mb-3 position-relative auth-pass-inputgroup">
                      <Label htmlFor="account-password" className="form-label">
                        {hasPassword
                          ? t("settings.account.fields.password.label")
                          : t("settings.account.fields.password.labelSetFirst")}
                      </Label>
                      <Input
                        id="account-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.values.password}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        invalid={!!(form.touched.password && form.errors.password)}
                      />
                      {renderPasswordToggle(
                        showPassword,
                        () => setShowPassword((value) => !value),
                        "account-password"
                      )}
                      {form.touched.password && form.errors.password ? (
                        <FormFeedback type="invalid">{form.errors.password}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-4 position-relative auth-pass-inputgroup">
                      <Label htmlFor="account-password-confirmation" className="form-label">
                        {t("settings.account.fields.passwordConfirmation.label")}
                      </Label>
                      <Input
                        id="account-password-confirmation"
                        name="password_confirmation"
                        type={showPasswordConfirmation ? "text" : "password"}
                        autoComplete="new-password"
                        value={form.values.password_confirmation}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                        invalid={
                          !!(
                            form.touched.password_confirmation &&
                            form.errors.password_confirmation
                          )
                        }
                      />
                      {renderPasswordToggle(
                        showPasswordConfirmation,
                        () => setShowPasswordConfirmation((value) => !value),
                        "account-password-confirmation"
                      )}
                      {form.touched.password_confirmation &&
                      form.errors.password_confirmation ? (
                        <FormFeedback type="invalid">
                          {form.errors.password_confirmation}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <Button
                      type="submit"
                      color="primary"
                      disabled={form.isSubmitting || !form.dirty}
                    >
                      {form.isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("settings.account.actions.saving")}
                        </>
                      ) : (
                        t("settings.account.actions.save")
                      )}
                    </Button>
                  </Form>
                )}
              </CardBody>
            </Card>

            {!loading ? (
              <Card className="mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <GoogleLogo size={20} />
                    {t("settings.account.google.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-3">
                    {t("settings.account.google.help")}
                  </p>
                  {googleLinked ? (
                    <>
                      <Alert color="success" className="mb-3">
                        {t("settings.account.google.linkedAs", {
                          email: googleEmail || t("settings.account.google.linkedUnknown"),
                        })}
                      </Alert>
                      {!hasPassword ? (
                        <Alert color="warning" className="mb-3">
                          {t("settings.account.google.unlinkNeedsPassword")}
                        </Alert>
                      ) : null}
                      <Button
                        color="danger"
                        outline
                        disabled={unlinkingGoogle || !hasPassword}
                        onClick={handleUnlinkGoogle}
                      >
                        {unlinkingGoogle ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("settings.account.google.unlinking")}
                          </>
                        ) : (
                          t("settings.account.google.unlink")
                        )}
                      </Button>
                    </>
                  ) : (
                    <GoogleAuthButton intent="link" />
                  )}
                </CardBody>
              </Card>
            ) : null}

            {!loading && memberships.length > 1 ? (
              <Card className="mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("settings.account.preferredStudio.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small">
                    {t("settings.account.preferredStudio.help")}
                  </p>
                  {preferredMessage ? (
                    <Alert color="success" className="py-2">
                      {preferredMessage}
                    </Alert>
                  ) : null}
                  <div className="mb-3">
                    <Label htmlFor="account-preferred-studio" className="form-label">
                      {t("settings.account.preferredStudio.label")}
                    </Label>
                    <Input
                      id="account-preferred-studio"
                      type="select"
                      value={preferredTenantId}
                      onChange={(event) => {
                        setPreferredTenantIdState(event.target.value);
                        setPreferredMessage(null);
                      }}
                    >
                      <option value="">
                        {t("settings.account.preferredStudio.none")}
                      </option>
                      {memberships.map((membership) => (
                        <option key={membership.tenant_id} value={membership.tenant_id}>
                          {membership.tenant_name ||
                            t("auth.select_studio.unnamed")}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <Button
                    color="primary"
                    outline
                    disabled={savingPreferred}
                    onClick={handleSavePreferredStudio}
                  >
                    {savingPreferred ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("settings.account.actions.saving")}
                      </>
                    ) : (
                      t("settings.account.preferredStudio.save")
                    )}
                  </Button>
                </CardBody>
              </Card>
            ) : null}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountSettings;
